import React, { useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { Rotate90DegreesCw, ZoomIn, ZoomOut, RestartAlt, CenterFocusStrong, CameraAlt, Visibility, VisibilityOff, Edit, Delete } from '@mui/icons-material';
import CameraCaptureModal from './modals/CameraCaptureModal';

interface PhotoUploadProps {
  photo?: string;
  photoRotation?: number;
  photoZoom?: number;
  photoFlipX?: number;
  photoPositionX?: number;
  photoPositionY?: number;
  /** Foto de paciente = dado sensível: embaça por padrão; o olho revela (privacidade em tela compartilhada). */
  blurred?: boolean;
  onToggleBlur?: () => void;
  /** Bloqueia seleção/câmera/edição (ex.: cadastro ainda não salvo — RN-113a exige o id). */
  disabled?: boolean;
  /** Mensagem exibida na área do editor quando bloqueado. */
  disabledMessage?: string;
  onPhotoChange: (photoData: {
    photo?: string;
    photoRotation?: number;
    photoZoom?: number;
    photoFlipX?: number;
    photoPositionX?: number;
    photoPositionY?: number;
  }) => void;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  photo,
  photoRotation = 0,
  photoZoom = 1,
  photoFlipX = 1,
  photoPositionX = 0,
  photoPositionY = 0,
  blurred = false,
  onToggleBlur,
  disabled = false,
  disabledMessage,
  onPhotoChange
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialZoom, setInitialZoom] = useState<number>(1);
  const [cameraOpen, setCameraOpen] = useState(false);

  // Foto capturada pela câmera entra igual à selecionada por arquivo (data URL -> quadro).
  const handleCameraCapture = (dataUrl: string) => {
    onPhotoChange({
      photo: dataUrl,
      photoRotation: 0,
      photoZoom: undefined,
      photoFlipX: 1,
      photoPositionX: 0,
      photoPositionY: 0,
    });
  };

  const handlePhotoMouseDown = (e: React.MouseEvent) => {
    if (!photo) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - photoPositionX,
      y: e.clientY - photoPositionY
    });
  };

  const handlePhotoMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !photo) return;
    e.preventDefault();

    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;

    // Limitar o movimento dentro da área do preview (250x250px)
    const maxX = 125;
    const maxY = 125;
    const minX = -125;
    const minY = -125;

    const clampedX = Math.max(minX, Math.min(maxX, newX));
    const clampedY = Math.max(minY, Math.min(maxY, newY));

    onPhotoChange({
      photo,
      photoRotation,
      photoZoom,
      photoFlipX,
      photoPositionX: clampedX,
      photoPositionY: clampedY
    });
  };

  const handlePhotoMouseUp = () => {
    setIsDragging(false);
  };

  const handlePhotoMouseLeave = () => {
    setIsDragging(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        onPhotoChange({
          photo: event.target?.result as string,
          photoRotation: 0,
          photoZoom: undefined, // Will be set by onLoad
          photoFlipX: 1,
          photoPositionX: 0,
          photoPositionY: 0
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.target as HTMLImageElement;

    const containerWidth = 250;
    const containerHeight = 250;
    const imgAspectRatio = img.naturalWidth / img.naturalHeight;
    const containerAspectRatio = containerWidth / containerHeight;

    let initialScale = 1;
    if (imgAspectRatio > containerAspectRatio) {
      // Image is wider than container - scale to fit width
      initialScale = containerWidth / img.naturalWidth;
    } else {
      // Image is taller than container - scale to fit height
      initialScale = containerHeight / img.naturalHeight;
    }

    // Always store the initial zoom for reset functionality
    setInitialZoom(initialScale);

    // Only set initial scale if it's not already set
    if (photoZoom === undefined || photoZoom === 1) {
      onPhotoChange({
        photo,
        photoRotation,
        photoZoom: initialScale,
        photoFlipX,
        photoPositionX,
        photoPositionY
      });
    }
  };

  const updatePhoto = (updates: Partial<{
    photo?: string;
    photoRotation?: number;
    photoZoom?: number;
    photoFlipX?: number;
    photoPositionX?: number;
    photoPositionY?: number;
  }>) => {
    onPhotoChange({
      photo,
      photoRotation,
      photoZoom,
      photoFlipX,
      photoPositionX,
      photoPositionY,
      ...updates
    });
  };

  return (
    <div className="photo-upload-component">
      <div
        className="photo-preview"
        onClick={() => !photo && !disabled && document.getElementById('photo-upload')?.click()}
        onMouseMove={handlePhotoMouseMove}
        onMouseUp={handlePhotoMouseUp}
        onMouseLeave={handlePhotoMouseLeave}
      >
        {photo ? (
          <>
            <img
              src={photo}
              alt="Foto do paciente"
              className="patient-photo"
              style={{
                transform: `translate(-50%, -50%) translate(${photoPositionX}px, ${photoPositionY}px) rotate(${photoRotation}deg) scale(${photoZoom}) scaleX(${photoFlipX})`,
                transformOrigin: 'center',
                transition: isDragging ? 'none' : 'transform 0.3s ease',
                cursor: blurred ? 'default' : (isDragging ? 'grabbing' : 'grab'),
                filter: blurred ? 'blur(16px)' : 'none'
              }}
              onMouseDown={blurred ? undefined : handlePhotoMouseDown}
              draggable={false}
              onLoad={handleImageLoad}
            />
            {onToggleBlur && (
              blurred ? (
                <button
                  type="button"
                  className="photo-reveal-overlay"
                  onClick={(e) => { e.stopPropagation(); onToggleBlur(); }}
                  title="Revelar foto"
                  aria-label="Revelar foto"
                >
                  <Visibility fontSize="large" />
                  <span>Revelar</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="photo-hide-toggle"
                  onClick={(e) => { e.stopPropagation(); onToggleBlur(); }}
                  title="Ocultar foto"
                  aria-label="Ocultar foto"
                >
                  <VisibilityOff fontSize="small" />
                </button>
              )
            )}
          </>
        ) : (
          <div className="no-photo-placeholder" style={disabled ? { cursor: 'default', opacity: 0.7 } : undefined}>
            <div className="photo-icon"><CameraAlt fontSize="large" /></div>
            <span>{disabled ? (disabledMessage || 'Foto indisponível') : 'Clique para adicionar foto'}</span>
          </div>
        )}
      </div>
      <input
        type="file"
        id="photo-upload"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Controles principais: três ícones redondos (alterar, câmera, remover), no padrão dos
          botões de ação do sistema — pedido do Hilton, 04/09: sem botões grandes nem caixa de
          instruções. */}
      <div className="photo-main-controls" style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
        <Tooltip title={photo ? 'Alterar foto' : 'Selecionar foto'}>
          <span>
            <IconButton
              size="small"
              onClick={() => document.getElementById('photo-upload')?.click()}
              disabled={disabled}
              sx={{ width: 36, height: 36, color: '#fff', backgroundColor: '#03B4C6', '&:hover': { backgroundColor: '#029AAB' }, '&.Mui-disabled': { backgroundColor: '#03B4C6', opacity: 0.4, color: '#fff' } }}
            >
              <Edit sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Tirar foto pela câmera">
          <span>
            <IconButton
              size="small"
              onClick={() => setCameraOpen(true)}
              disabled={disabled}
              sx={{ width: 36, height: 36, color: '#fff', backgroundColor: '#6c757d', '&:hover': { backgroundColor: '#5a6268' }, '&.Mui-disabled': { backgroundColor: '#6c757d', opacity: 0.4, color: '#fff' } }}
            >
              <CameraAlt sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          </span>
        </Tooltip>
        {photo && (
          <Tooltip title="Remover foto">
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                onClick={() => onPhotoChange({
                  photo: '',
                  photoRotation: 0,
                  photoZoom: 1,
                  photoFlipX: 1,
                  photoPositionX: 0,
                  photoPositionY: 0
                })}
                sx={{ width: 36, height: 36, color: '#fff', backgroundColor: '#dc3545', '&:hover': { backgroundColor: '#c82333' } }}
              >
                <Delete sx={{ fontSize: '1.1rem' }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </div>

      {/* Controles de edição */}
      {photo && (
        <div className="photo-edit-controls">
          <div className="edit-control-row">
            <button
              type="button"
              className="btn-photo-edit-small"
              title="Girar à esquerda"
              onClick={() => updatePhoto({ photoRotation: photoRotation - 90 })}
            >
              <Rotate90DegreesCw fontSize="small" />
            </button>
            <button
              type="button"
              className="btn-photo-edit-small"
              title="Girar à direita"
              onClick={() => updatePhoto({ photoRotation: photoRotation + 90 })}
            >
              <Rotate90DegreesCw fontSize="small" style={{ transform: 'scaleX(-1)' }} />
            </button>
            <button
              type="button"
              className="btn-photo-edit-small"
              title="Zoom out"
              onClick={() => updatePhoto({ photoZoom: Math.max(0.1, photoZoom - 0.1) })}
            >
              <ZoomOut fontSize="small" />
            </button>
            <button
              type="button"
              className="btn-photo-edit-small"
              title="Zoom in"
              onClick={() => updatePhoto({ photoZoom: Math.min(5, photoZoom + 0.1) })}
            >
              <ZoomIn fontSize="small" />
            </button>
            <button
              type="button"
              className="btn-photo-edit-small"
              title="Reset"
              onClick={() => onPhotoChange({
                photo,
                photoRotation: 0,
                photoZoom: initialZoom,
                photoFlipX: 1,
                photoPositionX: 0,
                photoPositionY: 0
              })}
            >
              <RestartAlt fontSize="small" />
            </button>
            <button
              type="button"
              className="btn-photo-edit-small"
              title="Centralizar posição"
              onClick={() => updatePhoto({ photoPositionX: 0, photoPositionY: 0 })}
            >
              <CenterFocusStrong fontSize="small" />
            </button>
          </div>
        </div>
      )}

      {/* Só o motivo do bloqueio, quando houver; as instruções de formato e recorte saíram
          (o formato inválido é recusado com mensagem, e o recorte é visível no preview). */}
      {disabled && disabledMessage && (
        <div className="photo-instructions">
          <small>{disabledMessage}</small>
        </div>
      )}

      <CameraCaptureModal
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={handleCameraCapture}
      />
    </div>
  );
};

export default PhotoUpload;
